/**
 * AttorneyReviewNote — visible "Attorney-reviewed" byline + short compliance note.
 *
 * Purpose
 * -------
 * Answer-engine visibility rewards content with clear authorship, a review
 * date, and an explicit non-advice disclaimer. This block renders all three
 * in a compact, editorial format that sits gracefully at the foot of any
 * substantive legal-content page.
 *
 * Usage
 * -----
 *   <AttorneyReviewNote reviewed="February 2026" />
 *
 * Props
 * -----
 *   reviewed  A friendly date string ("February 2026"). Defaults to the site's
 *             current review month if omitted.
 *   variant   "light" (default, on ivory/linen sections) or "dark" (on navy).
 *   compact   When true, uses a tighter one-line layout without the disclaimer
 *             paragraph — useful when a page already carries a full disclaimer.
 *
 * Notes
 * -----
 *   • No guarantees, no board-certification implications, no client facts.
 *   • Rendered content is intentionally text-only so it appears verbatim in
 *     server-rendered HTML for OAI-SearchBot / ChatGPT search discovery.
 */
export default function AttorneyReviewNote({
  reviewed = "February 2026",
  variant = "light",
  compact = false,
}) {
  const dark = variant === "dark";
  const label = dark ? "text-ivory/60" : "text-navy/55";
  const rule = dark ? "border-brass/25" : "border-brass/30";
  const body = dark ? "text-ivory/55" : "text-navy/60";

  return (
    <div
      className={`mt-16 pt-6 border-t ${rule}`}
      data-testid="attorney-review-note"
      role="contentinfo"
      aria-label="Attorney review and content disclaimer"
    >
      <div
        className={`text-[10px] uppercase tracking-[0.32em] font-medium ${label}`}
      >
        <span className="text-brass">★</span>{" "}
        <span>Attorney-reviewed:</span>{" "}
        <span className={dark ? "text-ivory/85" : "text-navy/80"}>
          Darryl V. Pratt, Esq., CPA
        </span>{" "}
        <span aria-hidden="true">·</span>{" "}
        <time
          dateTime={friendlyToIso(reviewed)}
          className={dark ? "text-ivory/85" : "text-navy/80"}
        >
          {reviewed}
        </time>
      </div>
      {!compact && (
        <p className={`mt-3 text-xs leading-relaxed ${body}`}>
          The information on this page is general information about Texas law
          and is not legal, tax, accounting, or financial advice. Reading this
          page or contacting the firm does not create an attorney-client
          relationship. An attorney-client relationship is established only
          through a written engagement agreement signed by the client and the
          firm. Prior results do not guarantee a similar outcome. Attorney
          Advertising — Pratt Law Group, PLLC d/b/a LONE STAR ★ COUNSEL.
        </p>
      )}
    </div>
  );
}

// --- helpers ---------------------------------------------------------------

/**
 * Convert a friendly review string ("February 2026") to an ISO date suitable
 * for the <time datetime> attribute. Defaults to the first of the month.
 * If the string can't be parsed, returns undefined so React drops the attr.
 */
function friendlyToIso(s) {
  if (!s) return undefined;
  const d = new Date(`1 ${s}`);
  if (isNaN(d.getTime())) return undefined;
  return d.toISOString().slice(0, 10);
}
